import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CircleCheck, FileSignature, FileText, MessagesSquare, Wallet } from 'lucide-react';
import { Selo, classesBotao } from '../../../web/src/componentes/base';
import { Estado, Vazio } from '../../../web/src/componentes/estados';
import { dinheiro, textoLivre } from '../../../web/src/nucleo/formato';
import { pget } from '../api';
import { nomeEmpresa, primeiroNome, useSessao } from '../sessao';
import { competenciaCurta, dataCurta } from './comum';

export type ItemHoje =
  | { tipo: 'fatura'; id: string; titulo: string; quando: string; vencida: boolean; valor_centavos: number; moeda: string; pode_pagar: boolean }
  | { tipo: 'entrega'; id: string; titulo: string; quando: string; area: string; competencia: string | null }
  | { tipo: 'mensagem'; id: string; titulo: string; quando: string; quantidade: number }
  /** Pacote 2 (Documentos e assinatura): documento esperando a assinatura do cliente. */
  | { tipo: 'assinatura'; id: string; titulo: string; quando: string; prazo: string | null };

/**
 * Review r23 visual (M1): uma tela, um primário. Só a fatura pagável mais urgente (a vencida há mais tempo; sem vencida, a
 * que vence primeiro) leva o "Pagar" primário; as outras pagáveis ficam secundárias, como o "Ver".
 */
export function faturaPrimaria(itens: ItemHoje[]): string | null {
  const pagaveis = itens.filter((i): i is Extract<ItemHoje, { tipo: 'fatura' }> => i.tipo === 'fatura' && i.pode_pagar);
  pagaveis.sort((a, b) => (a.vencida === b.vencida ? a.quando.localeCompare(b.quando) : a.vencida ? -1 : 1));
  return pagaveis[0]?.id ?? null;
}

/** "Hoje": o que depende do cliente agora, numa lista só e com a ação de um toque (plano 5.3). */
export function Hoje() {
  const { t, i18n } = useTranslation('portal');
  const s = useSessao();
  const q = useQuery({ queryKey: ['portal', 'hoje'], queryFn: () => pget<{ itens: ItemHoje[]; tudo_em_dia: boolean }>('/hoje') });
  const idioma = i18n.language.slice(0, 2);
  const nome = primeiroNome(s);
  const primaria = q.data ? faturaPrimaria(q.data.itens) : null;
  return (
    <div className="flex flex-col gap-4">
      {/* Review r29 robustez (O3): nome sem espaço (primeiro nome ou empresa) quebra na largura, como as mensagens; a 390 dava rolagem lateral. */}
      <div>
        <p className="kicker break-words">{nomeEmpresa(s)}</p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight break-words">{nome ? t('hoje.saudacao', { nome }) : t('hoje.titulo')}</h1>
      </div>
      <Estado consulta={q}>
        {(d) =>
          d.tudo_em_dia ? (
            <div className="rounded-xl border border-borda bg-sup-1">
              <Vazio icone={<CircleCheck className="size-5 text-verde" aria-hidden />} titulo={t('hoje.tudoEmDia')} texto={t('hoje.tudoEmDiaTexto')} />
            </div>
          ) : (
            <section aria-labelledby="hoje-lista" className="rounded-xl border border-borda bg-sup-1">
              <h2 id="hoje-lista" className="border-b border-borda px-4 py-3 text-sm font-semibold">
                {t('hoje.lista')}
              </h2>
              <ul className="divide-y divide-borda" data-hoje>
                {d.itens.map((i) => (
                  <li key={`${i.tipo}-${i.id}`} className="flex items-center gap-3 px-4 py-3" data-item-hoje={i.tipo}>
                    <span className={`flex size-9 shrink-0 items-center justify-center rounded-lg border ${i.tipo === 'fatura' && i.vencida ? 'border-perigo text-perigo' : 'border-borda text-tinta-2'}`} aria-hidden>
                      {i.tipo === 'fatura' ? <Wallet className="size-4" /> : i.tipo === 'entrega' ? <FileText className="size-4" /> : i.tipo === 'assinatura' ? <FileSignature className="size-4" /> : <MessagesSquare className="size-4" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      {i.tipo === 'fatura' && (
                        <>
                          <p className="truncate text-sm font-medium">{t('hoje.fatura', { numero: i.titulo })}</p>
                          <p className={`text-xs ${i.vencida ? 'text-perigo' : 'text-tinta-3'}`}>
                            <span className="numero font-semibold text-tinta-1">{dinheiro(i.valor_centavos, i.moeda)}</span> · {i.vencida ? t('hoje.vencida', { data: dataCurta(i.quando, idioma) }) : t('hoje.vence', { data: dataCurta(i.quando, idioma) })}
                          </p>
                        </>
                      )}
                      {i.tipo === 'entrega' && (
                        <>
                          <p className="truncate text-sm font-medium">{textoLivre(i.titulo, s.empresa.nome)}</p>
                          <p className="flex flex-wrap items-center gap-2 text-xs text-tinta-3">
                            <Selo cor="acento">{t('hoje.entrega')}</Selo>
                            {t(`documentos.area.${i.area}`)}
                            {i.competencia ? ` · ${competenciaCurta(i.competencia, idioma)}` : ''}
                          </p>
                        </>
                      )}
                      {i.tipo === 'assinatura' && (
                        <>
                          <p className="truncate text-sm font-medium">{textoLivre(i.titulo, s.empresa.nome)}</p>
                          <p className="flex flex-wrap items-center gap-2 text-xs text-tinta-3">
                            <Selo cor="laranja">{t('hoje.assinatura')}</Selo>
                            {i.prazo ? t('assinar.ate', { data: dataCurta(i.prazo, idioma) }) : ''}
                          </p>
                        </>
                      )}
                      {i.tipo === 'mensagem' && (
                        <>
                          <p className="truncate text-sm font-medium">{t('hoje.mensagem', { count: i.quantidade })}</p>
                          <p className="truncate text-xs text-tinta-3">{i.titulo || t('mensagens.equipe')}</p>
                        </>
                      )}
                    </div>
                    {i.tipo === 'fatura' && (
                      <Link to={`/financeiro/${i.id}`} className={classesBotao(i.pode_pagar && i.id === primaria ? 'primario' : 'secundario', 'sm')}>
                        {i.pode_pagar ? t('hoje.pagar') : t('hoje.ver')}
                      </Link>
                    )}
                    {i.tipo === 'entrega' && (
                      <Link to="/documentos" className={classesBotao('secundario', 'sm')}>
                        {t('hoje.abrir')}
                      </Link>
                    )}
                    {i.tipo === 'assinatura' && (
                      <Link to="/documentos?aba=assinar" className={classesBotao('secundario', 'sm')}>
                        {t('hoje.abrir')}
                      </Link>
                    )}
                    {i.tipo === 'mensagem' && (
                      <Link to="/mensagens" className={classesBotao('secundario', 'sm')}>
                        {t('hoje.responder')}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )
        }
      </Estado>
    </div>
  );
}
